import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchPosts } from "../../api";
import { useLanguage } from "../../context/LanguageContext";

export default function BlogSection() {
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const translations = {
    en: {
      tag: "Latest Insights",
      title: "From the",
      blog: "Blog",
      desc: "Stay updated with my latest thoughts on design, development, and more.",
      readMore: "Read Article",
      viewMore: "View More Articles",
    },
    fr: {
      tag: "Derniers Articles",
      title: "Depuis le",
      blog: "Blog",
      desc: "Restez informé de mes dernières réflexions sur le design, le développement et plus encore.",
      readMore: "Lire l'article",
      viewMore: "Voir plus d'articles",
    },
  };

  const t = translations[language];

  useEffect(() => {
    const getPosts = async () => {
      try {
        const data = await fetchPosts();
        setPosts(data.slice(0, 3));
      } catch (error) {
        console.error("Failed to fetch posts", error);
      } finally {
        setLoading(false);
      }
    };
    getPosts();
  }, []);

  const getPostTitle = (post) =>
    language === "fr" && post.titleFr ? post.titleFr : post.title;
  const getPostContent = (post) => {
    const content =
      language === "fr" && post.contentFr ? post.contentFr : post.content;
    return content.replace(/<[^>]*>/g, "").slice(0, 100) + "...";
  };

  if (loading && posts.length === 0) return null;
  if (!loading && posts.length === 0) return null;

  return (
    <div className="w-full py-12 bg-white" id="blog">
      <div className="mx-5 lg:mx-20 flex flex-col gap-2">
        <div className="text-center" data-aos="zoom-in">
          <h1 className="font-Poppins font-medium text-slate-400 uppercase tracking-widest text-sm mb-2">
            {t.tag}
          </h1>
          <h2 className="text-3xl lg:text-4xl font-Poppins font-bold text-[#131315]">
            {t.title} <span className="text-[#3aa1ff]">{t.blog}</span>
          </h2>
          <p className="text-gray-500 mt-4 font-Inter max-w-2xl mx-auto">
            {t.desc}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 mt-8 lg:grid-cols-3 gap-8">
          {posts.map((post, index) => (
            <Link
              to={`/blog/${post.slug}`}
              key={post.id}
              data-aos="fade-up"
              data-aos-delay={index * 100}
              className="group bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 flex flex-col"
            >
              <div className="relative aspect-video overflow-hidden bg-gray-50">
                {post.coverImage ? (
                  <img
                    src={post.coverImage}
                    alt={getPostTitle(post)}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-12 h-12"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25M16.5 7.5V18a2.25 2.25 0 0 0 2.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 0 0 2.25 2.25h13.5M6 7.5h3v3H6v-3Z"
                      />
                    </svg>
                  </div>
                )}
              </div>

              <div className="p-8 flex flex-col flex-1">
                <span className="text-[#3aa1ff]/60 text-xs font-Inter font-bold mb-3 uppercase tracking-widest">
                  {new Date(post.createdAt).toLocaleDateString(
                    language === "en" ? "en-US" : "fr-FR",
                    { month: "long", day: "numeric" },
                  )}
                </span>
                <h3 className="font-Poppins font-bold text-xl text-[#131315] mb-4 group-hover:text-[#3aa1ff] transition-colors line-clamp-2">
                  {getPostTitle(post)}
                </h3>
                <p className="text-gray-500 text-sm font-Inter line-clamp-3 mb-6 flex-1 leading-relaxed">
                  {getPostContent(post)}
                </p>
                <div className="flex items-center text-[#3aa1ff] font-Poppins font-bold text-sm">
                  {t.readMore}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="flex justify-center mt-10" data-aos="fade-up">
          <Link
            to="/blog"
            className="bg-[#3aa1ff] text-white px-8 py-2.5 rounded-full font-Poppins font-semibold shadow-lg shadow-blue-500/25 hover:bg-[#288ce6] transition-all hover:shadow-blue-500/40 transform hover:-translate-y-1 active:scale-95"
          >
            {t.viewMore}
          </Link>
        </div>
      </div>
    </div>
  );
}
